import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { dataCache, idleManager } from "../lib/cache";
import { toast } from "sonner";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isLoading, setIsLoading] = useState(true);

  // Idle logout handler
  const handleIdleLogout = useCallback(() => {
    if (token && user) {
      toast.info("Logged out due to inactivity", { duration: 5000 });
      logout();
    }
  }, [token, user]);

  // Start idle monitoring when user is logged in
  useEffect(() => {
    if (user && token) {
      idleManager.start(handleIdleLogout);
    } else {
      idleManager.stop();
    }
    return () => idleManager.stop();
  }, [user, token, handleIdleLogout]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth check failed:", error);
          // If token is invalid, clear it but keep remembered credentials
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          dataCache.clear(); // Clear cached data on auth failure
          
          // Auto-login if remember me was enabled
          const rememberedEmail = localStorage.getItem("rememberedEmail");
          const rememberedPassword = localStorage.getItem("rememberedPassword");
          const rememberMe = localStorage.getItem("rememberMe") === "true";
          
          if (rememberMe && rememberedEmail && rememberedPassword) {
            try {
              const loginResponse = await axios.post(`${API_URL}/api/auth/login`, {
                email: rememberedEmail,
                password: rememberedPassword,
              });
              const { access_token, user: userData } = loginResponse.data;
              localStorage.setItem("token", access_token);
              setToken(access_token);
              setUser(userData);
            } catch (loginError) {
              console.error("Auto-login failed:", loginError);
              // Clear remembered credentials if auto-login fails
              localStorage.removeItem("rememberedEmail");
              localStorage.removeItem("rememberedPassword");
              localStorage.removeItem("rememberMe");
            }
          }
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Clear old cache on fresh login to get latest data
    dataCache.clear();
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    // Clear remembered credentials on manual logout
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    localStorage.removeItem("rememberMe");
    // Clear cached data
    dataCache.clear();
    idleManager.stop();
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider
      value={{ user, token, isLoading, login, logout, getAuthHeaders }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
